import { createContext, useContext } from 'react';

/**
 * Acceso al estado de sesión.
 *
 * El contexto y el hook viven aquí, y el provider en `AuthProvider.jsx`.
 *
 * No es solo orden: si un archivo exporta a la vez un componente y una función
 * normal, el Fast Refresh de Vite deja de funcionar para ese archivo, y cada
 * guardado recarga la página entera perdiendo el estado de todos los
 * componentes.
 */

/**
 * El valor por defecto es `null` a propósito. Con `undefined` sería imposible
 * distinguir "el provider no está montado" de "la sesión vale undefined", y el
 * error aparecería tres componentes más allá, en el peor sitio.
 */
export const AuthContext = createContext(null);

/**
 * Estado de sesión del usuario actual.
 *
 * @returns {{
 *   usuario: import('@academy/shared').PublicUser | null,
 *   estado: 'cargando' | 'autenticado' | 'anonimo',
 *   aviso: string | null,
 *   login: (credenciales: { email: string, password: string }) => Promise<import('@academy/shared').PublicUser>,
 *   register: (datos: { name: string, email: string, password: string }) => Promise<import('@academy/shared').PublicUser>,
 *   logout: () => Promise<void>,
 *   logoutEverywhere: () => Promise<void>,
 *   updateProfile: (cambios: Record<string, unknown>) => Promise<import('@academy/shared').PublicUser>,
 *   changePassword: (cambios: { currentPassword: string, newPassword: string }) => Promise<unknown>,
 *   deleteAccount: () => Promise<void>,
 *   dismissAviso: () => void,
 * }}
 */
export function useAuth() {
  const contexto = useContext(AuthContext);

  if (contexto === null) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  }

  return contexto;
}
